import duckify from '../../tools/duckify';
import { get as getSatellite } from '../../../common/satellites';
import { includes } from 'lodash';

export const namespace = 'cassie';
export const store = 'acquisition';

export const Types = duckify(namespace, store, [
  'SET_SATELLITE',
  'SET_MISSION_FALLBACK',
  'SET_AOI',
  'SET_PERIOD',
  'SET_AVAILABLE_DATES',
  'LOAD_AVAILABLE_IMAGES',
  'ACQUIRE_IMAGE',
  'ACQUIRE_IMAGE_INTERTIDAL',
  'REQUEST_AOI',
  'LOAD_TEST_STATE',
  'LOAD_THUMBNAILS',
  'CANCEL_THUMBNAILS',
  'INSERT_METADATA',
  'RESET',
]);

export const Actions = {
  /**
   * Defines the satellite to be used for this acquisition.
   * @see satellites.js
   * @param {Number} satelliteIndex the index of the satellite
   */
  setSatellite: (satelliteIndex, type = 'null', includeLandsat7 = false) => {
    window.sessionStorage.setItem('selectedSatellite', getSatellite(satelliteIndex).name);
    if (type) window.sessionStorage.setItem('typeSatellite', type);
    window.sessionStorage.setItem('selectedSatelliteIndex', satelliteIndex);
    return {
      type: Types.SET_SATELLITE,
      payload: {
        satellite: getSatellite(satelliteIndex, includeLandsat7),
        satelliteIndex,
      },
    };
  },

  setTrainingData: (trainingData) => {
    window.sessionStorage.setItem('trainingData', JSON.stringify(trainingData));
  },

  setTrainingDate: (trainingDate) => {
    window.sessionStorage.setItem('trainingDate', JSON.stringify(trainingDate));
  },

  /**
   * Defines that the main mission cannot be used and use a secondary collection instead
   */
  setMissionFallback: (mission) => ({
    type: Types.SET_MISSION_FALLBACK,
    payload: { mission },
  }),

  /**
   * Defines the Area Of Interest to be used for this acquisition.
   * @param {*} overlay the overlay from Google Maps
   * @param {Coordinate[]} coordinates the coordinates of the area
   * @param {*} geometry the geometry of the AOI from Earth Engine
   * @param {String} [kmlFileName] the imported KML file's name, kept so the AOIChooser step can
   * show it again if the user navigates back to it after already choosing an AOI
   */
  setAOI: (overlay, coordinates, geometry, kmlFileName) => ({
    type: Types.SET_AOI,
    payload: { overlay, coordinates, geometry, kmlFileName },
  }),

  /**
   * Begins downloading the list of available images for the currently selected
   * satellite, storing it in the state afterwards.
   * @param {String} [startDate] restricts the search to images on/after this date; omit to search the mission's entire history
   * @param {String} [endDate] restricts the search to images on/before this date; omit to search up to today
   */
  loadAvailableImages: (startDate, endDate) => ({
    type: Types.LOAD_AVAILABLE_IMAGES,
    payload: { startDate, endDate },
  }),

  /**
   * Sets the list of available dates for the current satellite.
   * @param {String[]} dates the list of dates, formatted as YYYY-mm-dd HH:mm:ss
   */
  setAvailableDates: (dates, missions) => {
    const normalizedDates = dates.map((img, idx) => {
      const normalized = {
        ...img,
        content: img.content !== undefined ? img.content : 0,
      };
      return normalized;
    });

    return {
      type: Types.SET_AVAILABLE_DATES,
      payload: { dates: normalizedDates, missions },
    };
  },

  /**
   * Defines the time period to be used in the image acquisition step
   * @param {Number} start the timestamp of the starting date
   * @param {Number} end the timestamp of the ending date
   */
  setPeriod: (start, end) => ({
    type: Types.SET_PERIOD,
    payload: { start, end },
  }),

  // Loads an image from the orbital cycle started at the specified date.
  acquireImage: (missionName, date, ...extras) => ({
    type: Types.ACQUIRE_IMAGE,
    payload: { missionName, date, extras },
  }),

  // Intertidal-only variant: loads a single image already clipped to the AOI.
  acquireImageIntertidal: (missionName, date, ...extras) => ({
    type: Types.ACQUIRE_IMAGE_INTERTIDAL,
    payload: { missionName, date, extras },
  }),

  requestAOI: () => ({
    type: Types.REQUEST_AOI,
  }),

  loadThumbnails: (images) => ({
    type: Types.LOAD_THUMBNAILS,
    payload: { images },
  }),

  cancelThumbnails: () => ({
    type: Types.CANCEL_THUMBNAILS,
  }),

  insertMetadata: (missionName, date, metadata) => ({
    type: Types.INSERT_METADATA,
    payload: { missionName, date, metadata },
  }),

  /**
   * Loads the default testing state into the state tree.
   */
  loadTestState: () => ({
    type: Types.LOAD_TEST_STATE,
  }),

  /**
   * Clears state.acquisition back to INITIAL_STATE.
   */
  reset: () => ({
    type: Types.RESET,
  }),
};

export default Actions;
